/**
 * <GuestSearchPanel> · ADR-FO-1 §D3 + AR-5
 * ────────────────────────────────────────────────────────────────
 * Universal guest lookup. Consumed platform-wide (F2 shortcut).
 * Pure UI — parent supplies results via `results` + `onQueryChange`.
 * Supports recent-searches + recent-guests memory.
 */
import React, { useEffect, useRef } from "react";
import {
  Clock, User, Loader2, X as XIcon,
} from "lucide-react";
import { LargeSearchBox, OperationalCard, StatusChip } from "./index";

export function GuestSearchPanel({
  query,
  onQueryChange,
  results = [],        // [{id, type, label, sublabel, hotel_id, status?}]
                        // type ∈ guest|reservation|stay|folio|invoice|room
  loading = false,
  recentSearches = [], // string[]
  recentGuests = [],   // [{id, label, sublabel, ...}]
  onSelect,
  onClear,
  placeholder = "Search by name, room, phone, reservation, folio, passport...",
  autoFocus = true,
  emptyLabel = "Type to search",
  noResultsLabel = "No matches found",
  testid = "guest-search-panel",
}) {
  const containerRef = useRef(null);
  useEffect(() => {
    // Trap Escape to clear
    const onKey = (e) => {
      if (e.key === "Escape") onClear?.();
    };
    const el = containerRef.current;
    el?.addEventListener("keydown", onKey);
    return () => el?.removeEventListener("keydown", onKey);
  }, [onClear]);

  const hasQuery = query && query.trim().length > 0;

  return (
    <div ref={containerRef}
      data-testid={testid}
      className="space-y-3">
      <LargeSearchBox
        value={query}
        onChange={onQueryChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onClear={onClear}
        testid={`${testid}-input`}/>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-[13px] px-1"
              data-testid={`${testid}-loading`}>
          <Loader2 className="w-4 h-4 animate-spin"/>
          Searching…
        </div>
      )}

      {/* Empty state — no query yet: show recent */}
      {!hasQuery && !loading && (
        <div className="space-y-4" data-testid={`${testid}-empty-state`}>
          {recentSearches.length > 0 && (
            <div>
              <SectionLabel icon={Clock} label="Recent Searches"/>
              <div className="flex flex-wrap gap-1.5">
                {recentSearches.slice(0, 8).map((s, i) => (
                  <button key={i}
                    onClick={() => onQueryChange?.(s)}
                    data-testid={`${testid}-recent-search-${i}`}
                    className="min-h-[36px] px-3 py-1.5 rounded-full border border-slate-300 bg-white
                                 text-[13px] text-slate-700 hover:border-sky-500 hover:bg-sky-50">
                    {s}
                    <button
                      type="button"
                      className="ml-2 opacity-0"
                      aria-hidden="true"><XIcon className="w-3 h-3 inline"/></button>
                  </button>
                ))}
              </div>
            </div>
          )}
          {recentGuests.length > 0 && (
            <div>
              <SectionLabel icon={User} label="Recent Guests"/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {recentGuests.slice(0, 6).map(g => (
                  <ResultRow key={g.id} row={{ ...g, type: "guest" }}
                                onSelect={onSelect}
                                testid={`${testid}-recent-guest-${g.id}`}/>
                ))}
              </div>
            </div>
          )}
          {recentSearches.length === 0 && recentGuests.length === 0 && (
            <div className="py-12 text-center text-slate-500 text-[14px]"
                    data-testid={`${testid}-hint`}>
              {emptyLabel}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {hasQuery && !loading && (
        results.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-[14px] border-2 border-dashed border-slate-200 rounded-xl"
                  data-testid={`${testid}-no-results`}>
            {noResultsLabel}
          </div>
        ) : (
          <div className="space-y-2" data-testid={`${testid}-results`}>
            {results.map(r => (
              <ResultRow key={`${r.type}-${r.id}`}
                            row={r}
                            onSelect={onSelect}
                            testid={`${testid}-result-${r.type}-${r.id}`}/>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
      {Icon && <Icon className="w-3 h-3"/>}
      <span>{label}</span>
    </div>
  );
}

const _TYPE_TONE = {
  guest: "active", reservation: "ready", stay: "active", folio: "pending",
  invoice: "pending", room: "ready",
};

function ResultRow({ row, onSelect, testid }) {
  const tone = _TYPE_TONE[row.type] || "unknown";
  return (
    <OperationalCard
      padded={false}
      testid={testid}
      onClick={() => onSelect?.(row)}
    >
      <div className="p-3 flex items-center gap-3">
        <StatusChip status={tone} label={row.type} size="sm"/>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium truncate">{row.label}</div>
          {row.sublabel && (
            <div className="text-[12px] text-slate-500 truncate">{row.sublabel}</div>
          )}
        </div>
      </div>
    </OperationalCard>
  );
}
